<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Provisions</a></li>
                    </ul>

                    <h3>Manage Provisions</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewProvision" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Provision</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="provisions"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description', 'id']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name, Description or ID"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No provisions found.
                        </template>

                        <template #loading>
                            Loading provisions. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Provision {{ this.provision.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="provision.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="provision.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="provision.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="provision.billingPlanName"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :disabled="!canUpdateProvision"
                            :loading="submitted" @click="updateProvision"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Provision {{ this.provision.name }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Provision?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteProvision"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Provision</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newProvision.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newProvision.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newProvision.billingPlan"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateProvision"
                            :loading="submitted" @click="createProvision"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Provision Components for {{ this.provision.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callViewProvisions" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callNewProvisionComponent" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Provision Component</span>
                                </a>
                            </li>
                        </ul>
                    </div>


                    <DataTable
                        :value="provisionComponents"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No provision components found.
                        </template>

                        <template #loading>
                            Loading provision components. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select"
                                             :model="getFilteredItemsComponents(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <Column field="name" header="Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Name</span>
                                {{ data.name }}
                            </template>
                        </Column>

                        <Column field="type" header="Type" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Type</span>
                                <span v-if="data.type === 'I'"
                                      :class="'component-badge status-' + data.type">Indirect</span>
                                <span v-if="data.type === 'D'"
                                      :class="'component-badge status-' + data.type">Direct</span>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Inactive</span>
                                <span v-if="data.status === 'A'"
                                      :class="'status-badge status-' + data.status">Active</span>
                            </template>
                        </Column>

                        <Column field="quantity" header="Quantity" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Quantity</span>
                                {{ data.quantity }}
                            </template>
                        </Column>

                        <Column field="used" header="Used" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Used</span>
                                {{ data.used }}
                            </template>
                        </Column>

                        <Column field="billingPlan" header="Billing Plan" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Billing Plan</span>
                                {{ data.billingPlanName }}
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Component for Provision {{ this.provision.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model="newProvisionComponent.name"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model="newProvisionComponent.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newProvisionComponent.type" :options="componentTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="quantity">Quantity</label>
                        <InputNumber id="quantity" v-model="newProvisionComponent.quantity"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newProvisionComponent.billingPlan"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateProvisionComponent"
                            :loading="submitted" @click="createProvisionComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Provision Component {{ this.provisionComponent.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="provisionComponent.name"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="provisionComponent.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="quantity">Quantity</label>
                        <InputText id="quantity" v-model.trim="provisionComponent.quantity"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="used">Used</label>
                        <InputText id="used" v-model.trim="provisionComponent.used"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="provisionComponent.type" :options="componentTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="provisionComponent.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="provisionComponent.billingPlanName"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :disabled="!canUpdateProvisionComponent"
                            :loading="submitted" @click="updateProvisionComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Provision Component for {{ this.provisionComponent.description }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Provision Component?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteProvisionComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import ProvisionService from "@/service/ProvisionService";
import ProvisionComponentService from "@/service/ProvisionComponentService";
import numeral from "numeral";
import BillingPlanService from "@/service/BillingPlanService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
    },
    async mounted() {
        await this.loadCompanyContext();

    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            filteredItemsComponentsCache: new Map(),
            provision: {},
            newProvision: {},
            provisions: null,
            originalProvisions: null,
            provisionComponents: null,
            provisionComponent: {},
            newProvisionComponent: {},
            filteredDiagnosticCodes: [],
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            filteredBillingPlans: [],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            componentTypes: [
                {name: 'Direct', code: 'D'},
                {name: 'Indirect', code: 'I'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            recurringTypes: [
                {name: 'None', code: 'N'},
                {name: 'Daily', code: 'D'},
                {name: 'Weekly', code: 'W'},
                {name: 'Bi-Weekly', code: 'B'},
                {name: 'Monthly', code: 'M'}
            ]
        }
    },
    watch: {
        provisions() {
            this.filteredItemsCache.clear();
        },
        provisionComponents() {
            this.filteredItemsComponentsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        canCreateProvision() {
            console.log("[ConfigurationProvisions][canCreateProvision] this.newProvision = ", this.newProvision);
            if ((this.newProvision.name) && (this.newProvision.description) &&
                (this.newProvision.status) ) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateProvision() {
            console.log("[ConfigurationProvisions][canUpdateProvision] this.provision = ", this.provision);
            if ((this.provision.name) && (this.provision.description) &&
                (this.provision.status) ) {
                return true;
            } else {
                return false;
            }
        },
        canCreateProvisionComponent() {
            if ((this.newProvisionComponent.name) && (this.newProvisionComponent.description) &&
                (this.newProvisionComponent.status) && (this.newProvisionComponent.type) &&
                (this.newProvisionComponent.quantity) ) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateProvisionComponent() {
            if ((this.provisionComponent.name) && (this.provisionComponent.description) &&
                (this.provisionComponent.status) && (this.provisionComponent.type) ) {
                return true;
            } else {
                return false;
            }
        },
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData, provisionsData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    ProvisionService.getProvisions(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.provisions = provisionsData.data;
                this.originalProvisions = [...this.provisions];

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.provisions = ", this.provisions);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);

            this.columnsDefault = [
                {field: 'name', header: 'Name', sortable: true},
                {field: 'description', header: 'Description', sortable: true},
                {field: 'billingPlanName', header: 'Billing Plan', sortable: true},
                {field: 'status', header: 'Status', sortable: true}
            ];

            this.columnsFullList = [
                {field: 'id', header: 'ID', sortable: true},
                {field: 'name', header: 'Name', sortable: true},
                {field: 'description', header: 'Description', sortable: true},
                {field: 'status', header: 'Status', sortable: true}
            ];

            this.selectedColumns = this.columnsDefault;

            this.standardFilters = [
                {field: 'active', header: 'Active'},
                {field: 'inactive', header: 'Inactive'}
            ];
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        searchBillingPlan(event) {
            console.log("[ConfigurationProvisions][searchBillingPlan] event.query = ", event.query);
            console.log("[ConfigurationProvisions][searchBillingPlan] this.filteredBillingPlans = ", this.filteredBillingPlans);
            console.log("[ConfigurationProvisions][searchBillingPlan] Getting billing plan data for company = ", this.profile.companyId);

            if (event.query === '') {
                console.log("[ConfigurationProvisions][searchBillingPlan] event.query is blank. So calling getActiveBillingPlans.");
                BillingPlanService.getActiveBillingPlans(this.profile.companyId)
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[ConfigurationProvisions][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[ConfigurationProvisions][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[ConfigurationProvisions][searchBillingPlan] Error = ", e);
                    });
            } else {
                console.log("[ConfigurationProvisions][searchBillingPlan] event.query is NOT blank. So calling searchBillingPlans.");
                BillingPlanService.searchBillingPlans(this.profile.companyId, event.query.toLowerCase())
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[ConfigurationProvisions][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[ConfigurationProvisions][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[ConfigurationProvisions][searchBillingPlan] Error = ", e);
                    });
            }
        },
        callNewProvision() {
            this.newProvision = {};
            this.newProvision.name = '';
            this.newProvision.description = '';
            this.newProvision.status = 'A';
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callUpdateProvision(data) {
            this.activeTab = 2;
            this.provision = data;
        },
        callDeleteProvision(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.provision = data;
        },
        callNewProvisionComponent() {
            this.newProvisionComponent = {};
            this.newProvisionComponent.name = '';
            this.newProvisionComponent.description = '';
            this.newProvisionComponent.type = 'D';
            this.newProvisionComponent.status = 'A';
            this.newProvisionComponent.quantity = 0;
            this.newProvisionComponent.used = 0;
            this.activeTab = 6;
        },
        callUpdateProvisionComponent(data) {
            this.activeTab = 7;
            this.provisionComponent = data;
        },
        callDeleteProvisionComponent(data) {
            this.activeTab = 8;
            this.confirmationResponse = 'N';
            this.provisionComponent = data;
        },
        async callViewProvisions() {
            this.activeTab = 1;
        },
        async callViewProvisionComponents(data) {
            this.provision = data;
            this.activeTab = 5;

            const [provisionComponentData] = await Promise.all([
                ProvisionComponentService.getProvisionComponentsForProvision(this.provision.id)
            ]);

            this.provisionComponents = provisionComponentData.data;

            console.log('[ConfigurationProvisions][callViewProvisionComponents] this.provisionComponents = ', this.provisionComponents);
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.provisions !== null) {

                if (isActive) {
                    this.provisions = this.originalProvisions.filter(provision => provision.status === 'A');
                } else if (isInactive) {
                    this.provisions = this.originalProvisions.filter(provision => provision.status === 'I');
                } else {
                    this.provisions = this.originalProvisions;
                }

            } else {
                // Handle the case when auditTrails is null
                this.provisions = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateProvision(data);
                }
            });

            items.push({
                label: 'View Components',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewProvisionComponents(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteProvision(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        getFilteredItemsComponents(data) {
            if (this.filteredItemsComponentsCache.has(data.id)) {
                return this.filteredItemsComponentsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateProvisionComponent(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteProvisionComponent(data);
                }
            });

            this.filteredItemsComponentsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[ConfigurationProvisions][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            }
        },
        async createProvision() {
            if (this.newProvision.name) {

                try {
                    this.submitted = true;

                    var tempBillingPlan = "";

                    try {
                        tempBillingPlan = this.newProvision.billingPlan.id;
                    } catch (error) {
                        tempBillingPlan = "";
                    }

                    console.log('[ConfigurationProvisions][createProvision] tempBillingPlan = ', tempBillingPlan);
                    console.log('[ConfigurationProvisions][createProvision] this.newProvision = ', this.newProvision);

                    var provisionDto = {
                        name: this.newProvision.name,
                        description: this.newProvision.description,
                        billingPlanId: tempBillingPlan,
                        status: 'A'
                    };

                    const response = await ProvisionService.createProvision(this.profile.companyId, provisionDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [provisionsData] = await Promise.all([
                        ProvisionService.getProvisions(this.profile.companyId)
                    ]);

                    this.provisions = provisionsData.data;
                    this.originalProvisions = provisionsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created provision',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create provision. Please try again.',
                        life: 3000
                    });
                } finally {
                    this.submitted = false;
                    this.activeTab = 1;
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateProvision() {
            this.submitted = true;

            if (this.provision.id) {

                try {
                    var tempBillingPlan = this.provision.billingPlanName;

                    try {
                        if (this.provision.billingPlanName.id == null) {
                            tempBillingPlan = this.provision.billingPlanId;
                        } else {
                            tempBillingPlan = this.provision.billingPlanName.id;
                        }

                    } catch (error) {
                        tempBillingPlan = this.provision.billingPlanId;
                    }

                    var provisionDto = {
                        name: this.provision.name,
                        description: this.provision.description,
                        billingPlanId: tempBillingPlan,
                        status: this.provision.status
                    };

                    const response = await ProvisionService.updateProvision(this.provision.id, provisionDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [provisionsData] = await Promise.all([
                        ProvisionService.getProvisions(this.profile.companyId)
                    ]);

                    this.provisions = provisionsData.data;
                    this.originalProvisions = provisionsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated provision',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update provision. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteProvision() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.provision.id) {

                    try {
                        const response = await ProvisionService.deleteProvision(this.provision.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [provisionsData] = await Promise.all([
                            ProvisionService.getProvisions(this.profile.companyId)
                        ]);

                        this.provisions = provisionsData.data;
                        this.originalProvisions = provisionsData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted provision',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete provision. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createProvisionComponent() {

            if (this.newProvisionComponent.name) {

                try {
                    this.submitted = true;

                    var tempBillingPlan = "";

                    try {
                        tempBillingPlan = this.newProvisionComponent.billingPlan.id;
                    } catch (error) {
                        tempBillingPlan = "";
                    }

                    console.log('[ConfigurationProvisions][createProvisionComponent] tempBillingPlan = ', tempBillingPlan);
                    console.log('[ConfigurationProvisions][createProvisionComponent] this.newProvisionComponent = ', this.newProvisionComponent);

                    var provisionComponentDto = {
                        name: this.newProvisionComponent.name,
                        description: this.newProvisionComponent.description,
                        quantity: this.newProvisionComponent.quantity,
                        type: this.newProvisionComponent.type,
                        billingPlanId: tempBillingPlan,
                        used: 0,
                        status: 'A'
                    };

                    const response = await ProvisionComponentService.createProvisionComponent(this.provision.id, provisionComponentDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [provisionComponentData] = await Promise.all([
                        ProvisionComponentService.getProvisionComponentsForProvision(this.provision.id)
                    ]);

                    this.provisionComponents = provisionComponentData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created provision component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create provision component. Please try again.',
                        life: 3000
                    });
                } finally {
                    this.submitted = false;
                    this.activeTab = 5;
                }
            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async updateProvisionComponent() {
            this.submitted = true;

            if (this.provisionComponent.id) {

                try {
                    var tempBillingPlan = this.provisionComponent.billingPlanName;

                    try {
                        if (this.provisionComponent.billingPlanName.id == null) {
                            tempBillingPlan = this.provisionComponent.billingPlanId;
                        } else {
                            tempBillingPlan = this.provisionComponent.billingPlanName.id;
                        }

                    } catch (error) {
                        tempBillingPlan = this.provisionComponent.billingPlanId;
                    }

                    var editProvisionComponentDto = {
                        id: this.provisionComponent.id,
                        name: this.provisionComponent.name,
                        description: this.provisionComponent.description,
                        provisionId: this.provisionComponent.provisionId,
                        quantity: this.provisionComponent.quantity,
                        used: this.provisionComponent.used,
                        billingPlanId: tempBillingPlan,
                        type: this.provisionComponent.type,
                        status: this.provisionComponent.status,
                    };

                    console.log("[MsConfigurationBilling][updateProvisionComponent] editProvisionComponentDto = ", editProvisionComponentDto);

                    const response = await ProvisionComponentService.updateProvisionComponent(this.provisionComponent.id, editProvisionComponentDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [provisionComponentData] = await Promise.all([
                        ProvisionComponentService.getProvisionComponentsForProvision(this.provision.id)
                    ]);

                    this.provisionComponents = provisionComponentData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated provision component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update provision component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async deleteProvisionComponent() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.provisionComponent.id) {

                    try {
                        const response = await ProvisionComponentService.deleteProvisionComponent(this.provisionComponent.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [provisionComponentData] = await Promise.all([
                            ProvisionComponentService.getProvisionComponentsForProvision(this.provision.id)
                        ]);

                        this.provisionComponents = provisionComponentData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted provision component',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete provision component. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 5;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .confirmation-badge, .component-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.confirmation-badge {
    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }
}

.component-badge {
    &.status-D {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }

}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
